import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SelectField from '../inputs/SelectField';

function mapLotToForm(lot) {
  return {
    sku: lot.sku ?? '',
    shape: lot.shape ?? '',
    length: lot.length != null ? String(lot.length) : '',
    width: lot.width != null ? String(lot.width) : '',
    totalCarat: String(lot.totalCarat ?? ''),
    availableCarat: String(lot.availableCarat ?? ''),
    pricePerCarat: String(lot.pricePerCarat ?? ''),
    caratPerStone: lot.caratPerStone != null ? String(lot.caratPerStone) : '',
    buyPricePerCarat: lot.buyPricePerCarat != null ? String(lot.buyPricePerCarat) : '',
    allowOrderOverStockOverride: lot.allowOrderOverStock === true
      ? 'true'
      : (lot.allowOrderOverStock === false ? 'false' : 'default'),
  };
}

/**
 * Edit lot dialog with local form state so typing does not re-render the inventory page.
 */
export default function EditLotDialog({
  open,
  lot,
  onClose,
  onSaved,
  shapeOptions = [],
  canSeeBuyPrices = false,
  isMobile = false,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !lot) {
      setForm(null);
      setError('');
      return;
    }
    setForm(mapLotToForm(lot));
    setError('');
  }, [open, lot?._id]); // eslint-disable-line react-hooks/exhaustive-deps -- reset when a different lot opens

  const setField = useCallback((key, value) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const shapeSelectOptions = useMemo(
    () => shapeOptions.map((s) => {
      const shape = String(s).toUpperCase();
      return { value: shape, label: shape };
    }),
    [shapeOptions],
  );

  const handleSave = async () => {
    if (!lot?._id || !form || loading) return;
    const totalCarat = parseFloat(form.totalCarat);
    const availableCarat = parseFloat(form.availableCarat);
    const pricePerCarat = parseFloat(form.pricePerCarat);
    if (isNaN(totalCarat) || totalCarat < 0 || isNaN(availableCarat) || availableCarat < 0 || isNaN(pricePerCarat) || pricePerCarat < 0) {
      setError('Total carat, available carat, and price per ct must be valid non-negative numbers');
      return;
    }
    const isRound = form.shape === 'ROUND';
    let length = form.length !== '' ? parseFloat(form.length) : null;
    let width = form.width !== '' ? parseFloat(form.width) : null;
    if (isRound && width != null && !Number.isNaN(width) && width >= 0) length = width;
    if (isRound && length != null && !Number.isNaN(length) && length >= 0) width = length;
    if (length == null || width == null || length < 0 || width < 0) {
      setError(isRound ? 'Width (diameter) is required and must be non-negative' : 'Length and width are required and must be non-negative');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        sku: form.sku?.trim() || undefined,
        shape: form.shape,
        length,
        width,
        totalCarat,
        availableCarat,
        pricePerCarat,
        caratPerStone: form.caratPerStone !== '' ? parseFloat(form.caratPerStone) : undefined,
        allowOrderOverStock: form.allowOrderOverStockOverride === 'true'
          ? true
          : (form.allowOrderOverStockOverride === 'false' ? false : null),
      };
      if (canSeeBuyPrices && form.buyPricePerCarat !== '') {
        payload.buyPricePerCarat = parseFloat(form.buyPricePerCarat);
      }
      await axios.put(`/api/non-certified-diamonds/${lot._id}`, payload);
      onClose();
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update lot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{t('inventory.editLot')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {form && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField fullWidth label="SKU (optional)" value={form.sku} onChange={(e) => setField('sku', e.target.value)} size="small" />
            <SelectField
              label="Shape"
              fullWidth
              size="small"
              value={form.shape}
              onChange={(next) => setField('shape', next)}
              options={shapeSelectOptions}
              emptyLabel=""
            />
            {form.shape === 'ROUND' ? (
              <TextField
                fullWidth
                label="Width / diameter (mm)"
                type="number"
                value={form.width}
                onChange={(e) => {
                  const next = e.target.value;
                  setForm((prev) => (prev ? { ...prev, width: next, length: next } : prev));
                }}
                inputProps={{ step: 0.1, min: 0 }}
                size="small"
              />
            ) : (
              <>
                <TextField fullWidth label="Length (mm)" type="number" value={form.length} onChange={(e) => setField('length', e.target.value)} inputProps={{ step: 0.1, min: 0 }} size="small" />
                <TextField fullWidth label="Width (mm)" type="number" value={form.width} onChange={(e) => setField('width', e.target.value)} inputProps={{ step: 0.1, min: 0 }} size="small" />
              </>
            )}
            <TextField fullWidth label="Total carat" type="number" value={form.totalCarat} onChange={(e) => setField('totalCarat', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" />
            <TextField fullWidth label="Available (ct)" type="number" value={form.availableCarat} onChange={(e) => setField('availableCarat', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" />
            <TextField fullWidth label="Price per ct" type="number" value={form.pricePerCarat} onChange={(e) => setField('pricePerCarat', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" />
            {canSeeBuyPrices && (
              <TextField fullWidth label="Buy price per ct (optional)" type="number" value={form.buyPricePerCarat} onChange={(e) => setField('buyPricePerCarat', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" />
            )}
            <TextField fullWidth label="Ct per stone (optional)" type="number" value={form.caratPerStone} onChange={(e) => setField('caratPerStone', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" />
            <SelectField
              label={t('settings.inventory.lots.allowOrderOverStockOverride')}
              fullWidth
              size="small"
              value={form.allowOrderOverStockOverride}
              onChange={(next) => setField('allowOrderOverStockOverride', next)}
              options={[
                { value: 'default', label: t('settings.inventory.lots.overrideDefault') },
                { value: 'true', label: t('settings.inventory.lots.overrideAllow') },
                { value: 'false', label: t('settings.inventory.lots.overrideDisallow') },
              ]}
              emptyLabel=""
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('inventory.cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || !form}>{t('inventory.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}
